<template>
  <b-dropdown :text="dropdownText" :class="{ 'inactive-item': isInactive }">
    <search-input
      v-if="showSearch"
      @updateSearch="searchOnList"
      :searchLabel="searchLabel"
    />
    <div class="custom-divider" v-if="showSearch"></div>
    <div
      class="list-wrapper"
      v-for="(list, key) in internalData"
      :key="list._id"
    >
      <b-dropdown-header v-if="internalShowHeaders">
        {{ key }}
      </b-dropdown-header>
      <b-dropdown-item-button
        @click="$emit('selectItem', item)"
        v-for="item in list"
        :key="item._id"
        :title="item.fullName || item.name"
      >
        <span :class="{ 'inactive-item': item.inactive }" v-html="item.name">
        </span>
        <span v-if="item.badge && item.badge.show" :class="item.badge.classes">
          {{ item.badge.text }}
        </span>
      </b-dropdown-item-button>
    </div>
  </b-dropdown>
</template>

<script>
import 'core-js/fn/array/flat-map'
import SearchInput from '@/components/Shared/SearchInput.vue'
import { mapObjIndexed } from 'ramda'

export default {
  components: {
    SearchInput,
  },
  props: {
    data: {
      type: Object,
      required: true,
      validator: function(data) {
        return (
          typeof data === 'object' &&
          Object.keys(data).some(key => Array.isArray(data[key]))
        )
      },
    },
    showHeaders: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedId: {
      type: [Number, String, Boolean],
      required: false,
    },
    showSeeAll: {
      type: Boolean,
      required: false,
      default: false,
    },
    decorateLabel: {
      type: Function,
      required: false,
      default: t => t,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    searchLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      internalShowHeaders: false,
      internalData: {},
    }
  },
  computed: {
    selected() {
      const flat = data => Object.values(data).flatMap(i => i)

      return (
        flat(this.data).find(({ _id }) => _id === this.selectedId) || {
          name: '',
        }
      )
    },

    dropdownText() {
      return this.decorateLabel(this.selected.name)
    },

    isInactive() {
      return this.selected.inactive || false
    },
  },
  watch: {
    data: {
      handler(data) {
        this.internalData = { ...data }
      },
      immediate: true,
    },
    showHeaders: {
      handler(data) {
        this.internalShowHeaders = data
      },
      immediate: true,
    },
  },
  methods: {
    searchOnList(text) {
      this.internalShowHeaders = text.length ? false : this.showHeaders
      this.internalData = this.searchInObject({ ...this.data }, text)
    },
    searchInObject(data, text) {
      return mapObjIndexed(arr => this.searchInArray(arr, text), {
        ...data,
      })
    },
    searchInArray(arr, text) {
      return arr.filter(({ name, fullName }) =>
        (fullName || name).toLowerCase().includes(text.toLowerCase())
      )
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';
$default-padding: 25px;
$default-margin: $default-padding - 10px;

.b-dropdown,
.list-wrapper {
  flex-grow: 1;
  max-width: 500px;

  @media (max-width: '1024px') {
    max-width: initial;
  }

  @media (max-width: '425px') {
    width: 100%;
  }

  li {
    list-style: none;
  }

  .btn {
    text-transform: none !important;
    box-shadow: 0px 2px 10px -1px #c3c4ca !important;
  }
  &.show > .btn-secondary.dropdown-toggle,
  .btn-secondary:not(:disabled):not(.disabled):active {
    background-color: map-get($theme-colors, 'white');
    border-color: map-get($theme-colors, 'gray01');
    color: map-get($theme-colors, 'primary');
    overflow: hidden;
  }

  &.show {
    & > .btn-secondary.dropdown-toggle {
      border-bottom: none;
      border-bottom-right-radius: 0px !important;
      border-bottom-left-radius: 0px !important;
      border-color: map-get($theme-colors, 'gray01');
    }
    > .btn-secondary.dropdown-toggle:focus {
      box-shadow: none;
    }
    .dropdown-toggle::after {
      transform: rotate(180deg);
    }
  }

  .dropdown-menu {
    position: relative;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    -moz-overflow-scrolling: touch;
    -ms-overflow-scrolling: touch;
    -o-overflow-scrolling: touch;
    // overflow-scrolling: touch;
    width: 100%;
    height: auto;
    max-height: 400px;
    margin: 0;
    border-style: solid;
    border-color: map-get($theme-colors, 'gray01');
    border-top: 0px;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    padding: 0px 0px 10px;
    transform: translate3d(0px, 48px, 0px) !important;

    .dropdown-header {
      color: #060001;
      font-size: 10px !important;
      padding: 15px 24px 2px;
      text-transform: uppercase !important;
    }

    .dropdown-item {
      color: map-get($theme-colors, 'gray03');
      cursor: pointer;
      display: flex;
      font-size: 13px;
      outline: 0 !important;
      padding: 0 24px;
      align-items: center;

      @include media-breakpoint-up(md) {
        font-size: 16px;
      }

      span:first-child {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
      }

      &:focus,
      &:hover {
        color: map-get($theme-colors, 'primary');
        background: map-get($theme-colors, 'gray08');
        outline-color: rgba(0, 0, 0, 0);
      }
    }

    &.show {
      border: 1px solid map-get($theme-colors, 'gray01');
      border-bottom-left-radius: 24px;
      border-bottom-right-radius: 24px;
      border-top: none;
      box-shadow: 0px 2px 0px 1px map-get($theme-colors, 'gray08');
      top: -6px !important;
      // width: 250px;
    }
  }

  .btn-secondary {
    &:not(:disabled):not(.disabled):active:focus {
      box-shadow: none;
    }

    &.dropdown-toggle {
      align-items: center;
      background-color: map-get($theme-colors, 'white');
      border: 1px solid map-get($theme-colors, 'gray01');
      box-shadow: none;
      color: map-get($theme-colors, 'primary');
      display: inline-block;
      font-size: 14px;
      font-weight: 700;
      justify-content: space-between;
      text-align: left;
      text-overflow: ellipsis;
      overflow: hidden;
      padding-left: $default-padding;
      padding-right: 44px;
      width: 250px;

      &::first-letter {
        text-transform: capitalize !important;
      }

      &:after {
        align-items: center;
        background: map-get($theme-colors, 'gray08');
        border: 1px solid map-get($theme-colors, 'gray01') !important;
        border-radius: 30px;
        content: '\F140';
        display: flex;
        font: normal normal normal 24px/1 'Material Design Icons';
        font-size: 16px;
        height: 34px;
        justify-content: center;
        margin: 0 !important;
        position: absolute;
        right: 5px;
        text-rendering: auto;
        top: 6px;
        transition: 100ms ease all;
        width: 34px;
        -webkit-font-smoothing: antialiased;

        @media (max-width: '575px') {
          height: 28px;
          width: 28px;
        }
      }

      @media (max-width: '425px') {
        width: 100%;
      }
    }
  }

  .search-input {
    margin: 0.8em 0.8em 0;
  }

  .custom-divider {
    border-top: 1px solid map-get($theme-colors, 'gray00');
    margin: 1em 1.5em 0;
  }
}

.inactive-item {
  color: map-get($theme-colors, 'gray01');
  &:hover {
    color: map-get($theme-colors, 'gray01');
  }
}
</style>
